import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  effect,
  input,
  output,
  signal,
} from '@angular/core';

export interface CountdownParts {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
}

/**
 * Four-tile countdown used on the partner workspace gate.
 * Emits `expired` once when the remaining time reaches zero.
 */
@Component({
  selector: 'app-gate-countdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      .cd-tile {
        border-radius: 0.75rem;
        text-align: center;
        padding: 0.75rem 0.375rem;
        background: var(--background-secondary);
        border: 1px solid var(--border-default);
      }
      .cd-value {
        font-size: 2rem;
        font-weight: 800;
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.02em;
        line-height: 1;
        color: var(--brand-primary);
      }
      .cd-label {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.16em;
        margin-top: 0.375rem;
        font-weight: 600;
        color: var(--text-secondary);
      }
    `,
  ],
  template: `
    <div class="w-full" role="timer" [attr.aria-label]="label()">
      <div class="grid grid-cols-4 gap-2 sm:gap-3">
        @for (unit of units; track unit.key) {
          <div class="cd-tile">
            <p class="cd-value">{{ parts()[unit.key] }}</p>
            <p class="cd-label">{{ unit.label }}</p>
          </div>
        }
      </div>
    </div>
  `,
})
export class GateCountdown implements OnInit, OnDestroy {
  readonly endsAt = input<string | null>(null);
  readonly expired = output<void>();

  readonly units = [
    { key: 'days' as const, label: 'Days' },
    { key: 'hours' as const, label: 'Hours' },
    { key: 'minutes' as const, label: 'Mins' },
    { key: 'seconds' as const, label: 'Secs' },
  ];

  readonly parts = signal<CountdownParts>({ days: '00', hours: '00', minutes: '00', seconds: '00' });
  readonly label = signal('Time remaining');

  private timer: ReturnType<typeof setInterval> | null = null;
  private emitted = false;

  constructor() {
    effect(() => {
      this.endsAt();
      this.emitted = false;
      this.tick();
    });
  }

  ngOnInit(): void {
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private tick(): void {
    const raw = this.endsAt();
    if (!raw) {
      this.parts.set({ days: '00', hours: '00', minutes: '00', seconds: '00' });
      this.label.set('Countdown not scheduled');
      return;
    }

    const end = Date.parse(raw);
    if (Number.isNaN(end)) {
      return;
    }

    const remaining = Math.max(0, end - Date.now());
    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.parts.set({
      days: days.toString().padStart(2, '0'),
      hours: hours.toString().padStart(2, '0'),
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0'),
    });
    this.label.set(
      remaining <= 0
        ? 'The scheduled window has ended'
        : `Launching in ${days} days ${hours} hours ${minutes} minutes ${seconds} seconds`,
    );

    if (remaining <= 0 && !this.emitted) {
      this.emitted = true;
      this.expired.emit();
    }
  }
}
